import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { identityFetch } from '../api/identity';
import { useMutation } from '../lib/use-api';
import { ErrorNotice } from './Feedback';

/**
 * Người dùng tự đổi mật khẩu.
 *
 * Trước đây chỉ admin đặt lại hộ được, nên ai quên mật khẩu cũng phải nhờ —
 * kể cả chính admin.
 *
 * Server THU HỒI MỌI PHIÊN sau khi đổi, gồm cả phiên đang mở. Nên đổi xong
 * phải đăng xuất ngay: giữ token cũ trên màn hình chỉ dẫn tới một loạt 401 khó
 * hiểu ở thao tác kế tiếp.
 */
export function ChangePasswordDialog({
  onClose,
  onChanged,
}: {
  onClose: () => void;
  /** Gọi sau khi đổi thành công — nơi gọi phải đăng xuất. */
  onChanged: () => void;
}): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  /**
   * Trả `true` khi đổi xong — KHÔNG đọc `change.error` sau `await`.
   *
   * `error` là React state, lượt render hiện tại còn giữ giá trị cũ. Ở màn này
   * hậu quả nặng nhất: nhập sai mật khẩu cũ vẫn kích hoạt `onChanged` và người
   * dùng bị đăng xuất trong khi mật khẩu chưa hề đổi.
   */
  const change = useMutation<void, boolean>(
    useCallback(async () => {
      await identityFetch<void>('/api/auth/change-password', {
        method: 'POST',
        body: { currentPassword, newPassword },
      });
      return true;
    }, [currentPassword, newPassword]),
  );

  // Khớp hai ô kiểm ở CLIENT, không gửi lên server: đây là chống gõ nhầm, không
  // phải kiểm bảo mật. Độ dài tối thiểu do server quyết (theo tenant).
  const mismatch = confirmPassword !== '' && newPassword !== confirmPassword;
  const ready = currentPassword !== '' && newPassword !== '' && !mismatch;

  const submit = async (): Promise<void> => {
    if (await change.run()) onChanged();
  };

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <h2>{t('password.heading')}</h2>

      <label className="field">
        <span>{t('password.current')}</span>
        <input
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
        />
      </label>

      <label className="field">
        <span>{t('password.new')}</span>
        <input
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
        />
      </label>

      <label className="field">
        <span>{t('password.confirm')}</span>
        <input
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
        />
        {mismatch ? <small className="hint">{t('password.mismatch')}</small> : null}
      </label>

      <small className="hint">{t('password.signOutWarning')}</small>

      {change.error ? <ErrorNotice error={change.error} /> : null}

      <footer className="dialog__footer">
        <button type="button" onClick={() => dialogRef.current?.close()}>
          {t('common:action.cancel')}
        </button>
        <button type="button" disabled={change.pending || !ready} onClick={() => void submit()}>
          {t('common:action.save')}
        </button>
      </footer>
    </dialog>
  );
}
